import { AgentAvatar } from "@/components/AgentAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useApi } from "@/hooks/use-api";
import { useAppState } from "@/store/useApp";
import { Agent, AgentBoard, AgentFile } from "@/types/agent";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useBlocker } from "react-router";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AddAgentBoardDialog } from "./AddAgentBoardDialog";
import { AddAgentContextDialog } from "./AddAgentContextDialog";
import { AgentBoardDialog } from "./AgentBoardDialog";
import { AgentEditorHeader } from "./AgentEditorHeader";
import { AgentFilesSection } from "./AgentFilesSection";
import { AgentPlaybookPreview } from "./AgentPlaybookPreview";
import { DeleteAgentDialog } from "./DeleteAgentDialog";
import { InstructionsEditor } from "./InstructionsEditor";
import { createBoardCode, createMediaCode } from "./agentAssetCode";

const normalizeAgentFiles = (files: AgentFile[] = []): AgentFile[] =>
  files.map((file) => {
    const { code: _code, ...fileWithoutCode } = file;
    const type = file.type || "context";
    const code =
      type === "media"
        ? _code || createMediaCode(file.name)
        : undefined;

    return {
      ...fileWithoutCode,
      type,
      ...(code ? { code } : {}),
    };
  });

const normalizeAgentBoards = (
  boards: (AgentBoard & { elements?: unknown })[] = []
): AgentBoard[] =>
  boards.map((board) => ({
    id: board.id,
    name: board.name,
    code: board.code || createBoardCode(board.name),
    state:
      typeof board.state === "string"
        ? board.state
        : JSON.stringify(Array.isArray(board.elements) ? board.elements : []),
    previewImagePath: board.previewImagePath ?? null,
  }));

export const AgentEditor = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const {
    createAgent,
    updateAgent,
    previewAgentPlaybook,
    uploadMedia,
    fetchAssessments,
    fetchCourses,
    fetchAssessmentSampleSubmission,
  } =
    useApi();

  const agents = useAppState((state) => state.agents || []);
  const assessments = useAppState((state) => state.assessments);
  const courses = useAppState((state) => state.courses);

  const isEditing = agentId && agentId !== "new";
  const currentAgent = isEditing ? agents.find((a) => a.id === agentId) : null;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [assessmentType, setAssessmentType] = useState<string>("hybrid");
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<string>("");
  const [sampleSubmission, setSampleSubmission] = useState<string | null>(null);
  const [sampleSubmissionError, setSampleSubmissionError] = useState(false);
  const [isLoadingSampleSubmission, setIsLoadingSampleSubmission] =
    useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [playbook, setPlaybook] = useState<string | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [agentFiles, setAgentFiles] = useState<AgentFile[]>([]);
  const [isUploadingContext, setIsUploadingContext] = useState(false);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [isAddContextDialogOpen, setIsAddContextDialogOpen] = useState(false);
  const [isAddBoardDialogOpen, setIsAddBoardDialogOpen] = useState(false);
  const [editingBoardId, setEditingBoardId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [boards, setBoards] = useState<AgentBoard[]>([]);
  const mediaUploadInputRef = useRef<HTMLInputElement>(null);

  const hasUnsavedChanges = useMemo(() => {
    if (currentAgent) {
      const isNameChanged = name !== currentAgent.name;
      const isDescriptionChanged = description !== currentAgent.description;
      const isInstructionsChanged = instructions !== currentAgent.instructions;
      const isTypeChanged = assessmentType !== (currentAgent.type || "hybrid");
      const areFilesChanged =
        JSON.stringify(agentFiles) !==
        JSON.stringify(normalizeAgentFiles(currentAgent.files));
      const areBoardsChanged =
        JSON.stringify(boards) !==
        JSON.stringify(normalizeAgentBoards(currentAgent.boards));

      return (
        isNameChanged ||
        isDescriptionChanged ||
        isInstructionsChanged ||
        isTypeChanged ||
        areFilesChanged ||
        areBoardsChanged
      );
    } else {
      return (
        name !== "" ||
        description !== "" ||
        instructions !== "" ||
        assessmentType !== "hybrid" ||
        agentFiles.length > 0 ||
        boards.length > 0
      );
    }
  }, [
    currentAgent,
    name,
    description,
    instructions,
    assessmentType,
    agentFiles,
    boards,
  ]);

  const blocker = useBlocker(
    useCallback(
      ({
        currentLocation,
        nextLocation,
      }: {
        currentLocation: { pathname: string };
        nextLocation: { pathname: string };
      }) =>
        hasUnsavedChanges &&
        !isSaving &&
        currentLocation.pathname !== nextLocation.pathname,
      [hasUnsavedChanges, isSaving]
    )
  );

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && !isSaving) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges, isSaving]);

  useEffect(() => {
    if (currentAgent) {
      setName(currentAgent.name);
      setDescription(currentAgent.description);
      setInstructions(currentAgent.instructions);
      setAgentFiles(normalizeAgentFiles(currentAgent.files));
      setBoards(normalizeAgentBoards(currentAgent.boards));
      setAssessmentType(currentAgent.type || "hybrid");
      if (currentAgent.playbook) {
        setPlaybook(currentAgent.playbook);
      } else {
        setPlaybook(null);
      }
      setEditingBoardId(null);
      return;
    }
    setName("");
    setDescription("");
    setInstructions("");
    setAssessmentType("hybrid");
    setSelectedAssessmentId("");
    setSampleSubmission(null);
    setSampleSubmissionError(false);
    setAgentFiles([]);
    setPlaybook(null);
    setBoards([]);
    setEditingBoardId(null);
  }, [currentAgent]);

  useEffect(() => {
    if (!assessments) {
      void fetchAssessments();
    }
    if (!courses) {
      void fetchCourses();
    }
  }, [assessments, courses, fetchAssessments, fetchCourses]);

  const groupedHybridAssessments = useMemo(() => {
    const courseMap = new Map(
      (courses || []).map((course) => [course.id, course.title])
    );
    const groups = new Map<
      string,
      {
        courseId: string;
        courseTitle: string;
        assessments: NonNullable<typeof assessments>;
      }
    >();

    for (const assessment of assessments || []) {
      if (assessment.type !== "hybrid") continue;
      const courseTitle = courseMap.get(assessment.courseId) || "Unassigned course";
      const existing = groups.get(assessment.courseId);
      if (existing) {
        existing.assessments.push(assessment);
      } else {
        groups.set(assessment.courseId, {
          courseId: assessment.courseId,
          courseTitle,
          assessments: [assessment],
        });
      }
    }

    return Array.from(groups.values()).sort((a, b) =>
      a.courseTitle.localeCompare(b.courseTitle)
    );
  }, [assessments, courses]);

  const selectedAssessment = useMemo(
    () =>
      (assessments || []).find(
        (assessment) => assessment.id === selectedAssessmentId
      ),
    [assessments, selectedAssessmentId]
  );

  const handleAssessmentTypeChange = (value: string) => {
    setAssessmentType(value);
    setPlaybook(null);
    if (value !== "hybrid") {
      setSelectedAssessmentId("");
      setSampleSubmission(null);
      setSampleSubmissionError(false);
    }
  };

  const handlePreviewAssessmentChange = (value: string) => {
    setSelectedAssessmentId(value);
    setPlaybook(null);
    setSampleSubmission(null);
    setSampleSubmissionError(false);
  };

  useEffect(() => {
    if (
      assessmentType !== "hybrid" ||
      !selectedAssessmentId ||
      !selectedAssessment?.hasSampleSubmission
    ) {
      setSampleSubmission(null);
      setSampleSubmissionError(false);
      setIsLoadingSampleSubmission(false);
      return;
    }

    let isCancelled = false;
    setSampleSubmission(null);
    setSampleSubmissionError(false);
    setIsLoadingSampleSubmission(true);
    fetchAssessmentSampleSubmission(selectedAssessmentId)
      .then((markdown) => {
        if (!isCancelled) {
          setSampleSubmission(markdown);
          setSampleSubmissionError(false);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setSampleSubmission(null);
          setSampleSubmissionError(true);
          console.error("Failed to load sample submission", error);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingSampleSubmission(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [
    assessmentType,
    fetchAssessmentSampleSubmission,
    selectedAssessment,
    selectedAssessmentId,
  ]);

  const uploadAgentFile = async (file: File, fileType: AgentFile["type"]) => {
    if (
      fileType === "media" &&
      !["image/jpeg", "image/png", "video/mp4", "video/mpeg"].includes(
        file.type
      )
    ) {
      toast.error("Interview files must be JPEG, PNG, MP4 or MPEG");
      return false;
    }

    if (
      fileType === "context" &&
      !["application/pdf", "image/jpeg", "image/png"].includes(file.type)
    ) {
      toast.error("Playbook files must be PDF, JPEG or PNG");
      return false;
    }

    if (fileType === "context") {
      setIsUploadingContext(true);
    } else {
      setIsUploadingMedia(true);
    }

    try {
      const response = await uploadMedia(file, fileType === "context");
      setAgentFiles((prev) => [
        ...prev,
        {
          name: response.filename,
          path: response.path,
          contentType: response.contentType,
          type: fileType,
          ...(fileType === "media"
            ? {
                code: createMediaCode(response.filename),
              }
            : {}),
        },
      ]);
      return true;
    } catch (error) {
      toast.error("Failed to upload file");
      console.error(error);
      return false;
    } finally {
      if (fileType === "context") {
        setIsUploadingContext(false);
      } else {
        setIsUploadingMedia(false);
      }
    }
  };

  const handleAddContextDialogChange = (open: boolean) => {
    setIsAddContextDialogOpen(open);
  };

  const handleDirectMediaUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file) return;

    const success = await uploadAgentFile(file, "media");
    if (success) {
      toast.success("File uploaded successfully");
    }
  };

  const handleAddContext = async ({
    mediaFile,
    contextFile,
  }: {
    mediaFile: File | null;
    contextFile: File | null;
  }) => {
    if (!contextFile && !mediaFile) {
      toast.error("Choose at least one file");
      return;
    }

    const results = await Promise.all([
      mediaFile ? uploadAgentFile(mediaFile, "media") : true,
      contextFile ? uploadAgentFile(contextFile, "context") : true,
    ]);

    if (results.every(Boolean)) {
      toast.success("File uploaded successfully");
      handleAddContextDialogChange(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Please enter a name for the agent");
      return;
    }

    if (!description.trim()) {
      toast.error("Please enter a description for the agent");
      return;
    }

    if (!instructions.trim()) {
      toast.error("Please enter instructions for the agent");
      return;
    }

    if (assessmentType === "standalone" && !playbook) {
      toast.error("Please generate a playbook before saving an oral agent");
      return;
    }

    setIsSaving(true);
    try {
      if (isEditing && currentAgent && agentId) {
        const updatedAgent: Agent = {
          ...currentAgent!,
          name,
          description,
          instructions,
          files: agentFiles,
          boards,
          playbook:
            assessmentType === "standalone" ? playbook || undefined : undefined,
        };

        await updateAgent(agentId, updatedAgent);
        toast.success("Agent updated successfully");
      } else {
        await createAgent({
          name,
          description,
          instructions,
          type: assessmentType as "hybrid" | "standalone",
          files: agentFiles,
          boards,
          playbook:
            assessmentType === "standalone" ? playbook || undefined : undefined,
        });
        toast.success("Agent created successfully");
      }
      navigate("/agents");
    } catch (error) {
      toast.error("Failed to save agent");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (currentAgent) {
      setName(currentAgent.name);
      setDescription(currentAgent.description);
      setInstructions(currentAgent.instructions);
      setAgentFiles(normalizeAgentFiles(currentAgent.files));
      setBoards(normalizeAgentBoards(currentAgent.boards));
      setPlaybook(currentAgent.playbook || null);
      setEditingBoardId(null);
    } else {
      setName("");
      setDescription("");
      setInstructions("");
      setAssessmentType("hybrid");
      setSelectedAssessmentId("");
      setSampleSubmission(null);
      setSampleSubmissionError(false);
      setAgentFiles([]);
      setPlaybook(null);
      setBoards([]);
      setEditingBoardId(null);
    }
  };

  const handleAddBoard = (boardName: string) => {
    const board: AgentBoard = {
      id: crypto.randomUUID(),
      name: boardName,
      code: createBoardCode(boardName),
      state: "[]",
      previewImagePath: null,
    };

    setBoards((prev) => [...prev, board]);
    setEditingBoardId(board.id);
    setIsAddBoardDialogOpen(false);
  };

  const handleUpdateBoard = (updatedBoard: AgentBoard) => {
    setBoards((prev) =>
      prev.map((board) => (board.id === updatedBoard.id ? updatedBoard : board))
    );
  };

  const handleRemoveBoard = (boardToRemove: AgentBoard) => {
    setBoards((prev) => prev.filter((board) => board.id !== boardToRemove.id));
    if (editingBoardId === boardToRemove.id) {
      setEditingBoardId(null);
    }
  };

  const handleTestAgent = async () => {
    setIsTesting(true);
    try {
      const playbookResult = await previewAgentPlaybook({
        type: assessmentType,
        instructions,
        files: agentFiles,
        boards,
        assessmentId:
          assessmentType === "hybrid" ? selectedAssessmentId : undefined,
      });

      setPlaybook(playbookResult);
      toast.success("Playbook generated successfully");
    } catch (error) {
      toast.error("Failed to generate playbook");
      console.error(error);
      setPlaybook(null);
    } finally {
      setIsTesting(false);
    }
  };

  const contextFiles = useMemo(
    () => agentFiles.filter((file) => file.type === "context"),
    [agentFiles]
  );

  const mediaFiles = useMemo(
    () => agentFiles.filter((file) => file.type === "media"),
    [agentFiles]
  );

  const isUploadingFiles = isUploadingContext || isUploadingMedia;
  const canPreviewPlaybook =
    assessmentType === "standalone" ||
    Boolean(selectedAssessment && selectedAssessment.hasSampleSubmission);
  const previewEmptyMessage =
    assessmentType === "hybrid"
      ? selectedAssessment
        ? selectedAssessment.hasSampleSubmission
          ? "Preview will appear here"
          : "Preview not available for this assessment"
        : "Select a hybrid assessment to generate a playbook preview"
      : "Preview will appear here";
  const sampleSubmissionMessage =
    sampleSubmissionError
      ? "Failed to load sample submission"
      : selectedAssessment
        ? selectedAssessment.hasSampleSubmission
          ? "Sample submission will appear here"
          : "Preview not available for this assessment"
        : selectedAssessmentId
          ? "Loading selected assessment..."
          : "Select a hybrid assessment to view its sample submission";
  const editingBoard =
    boards.find((board) => board.id === editingBoardId) ?? null;

  const handleRemoveFile = (fileToRemove: AgentFile) => {
    setAgentFiles((prev) =>
      prev.filter(
        (file) =>
          !(
            file.path === fileToRemove.path && file.type === fileToRemove.type
          )
      )
    );
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      <input
        ref={mediaUploadInputRef}
        type="file"
        accept="image/jpeg,image/png,video/mp4,video/mpeg"
        className="hidden"
        onChange={handleDirectMediaUpload}
      />
      <AddAgentContextDialog
        open={isAddContextDialogOpen}
        isUploading={isUploadingFiles}
        onOpenChange={handleAddContextDialogChange}
        onSubmit={handleAddContext}
      />
      <AddAgentBoardDialog
        open={isAddBoardDialogOpen}
        onOpenChange={setIsAddBoardDialogOpen}
        onSubmit={handleAddBoard}
      />
      <AgentBoardDialog
        board={editingBoard}
        open={Boolean(editingBoard)}
        onOpenChange={(open) => {
          if (!open) {
            setEditingBoardId(null);
          }
        }}
        onBoardChange={handleUpdateBoard}
        uploadPreviewImage={(file) => uploadMedia(file, false)}
      />
      <DeleteAgentDialog
        agentId={agentId!}
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onSuccess={() => {
          setIsSaving(true);
          navigate("/agents");
        }}
      />

      <Dialog
        open={blocker.state === "blocked"}
        onOpenChange={(open) => {
          if (!open) {
            blocker.reset?.();
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogDescription>
              You have unsaved changes. Are you sure you want to leave this page?
              All changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => blocker.reset?.()}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => blocker.proceed?.()}
            >
              Leave Page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AgentEditorHeader
        isEditing={Boolean(isEditing)}
        isSaving={isSaving}
        onBack={() => navigate("/agents")}
        onDelete={() => setIsDeleteDialogOpen(true)}
        onReset={handleReset}
        onSave={handleSave}
      />

      {/* Main content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="flex items-center gap-4 ml-4 mt-6 mb-4 shrink-0">
          <AgentAvatar
            agent={currentAgent || undefined}
            agentId={isEditing ? agentId : undefined}
            size="lg"
          />
          <div className="flex flex-col flex-1 min-w-0 gap-1">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Agent name"
              className="border-none shadow-none p-0 h-auto text-lg font-semibold focus-visible:ring-0 px-0"
            />
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Agent description"
              className="border-none shadow-none p-0 h-auto text-sm text-muted-foreground focus-visible:ring-0 px-0"
            />
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Left Column: Configuration */}
          <div className="w-1/2 flex flex-col p-4 pl-4 overflow-y-auto">
            <div className="space-y-6 flex-1 flex flex-col min-h-0">
              <div className="space-y-2">
                <Label htmlFor="agent-type">Agent type</Label>
                <Select
                  value={assessmentType}
                  onValueChange={handleAssessmentTypeChange}
                >
                  <SelectTrigger id="agent-type">
                    <SelectValue placeholder="Select agent type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hybrid">Hybrid assessment</SelectItem>
                    <SelectItem value="standalone">Oral assessment</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {assessmentType === "hybrid" && (
                <div className="space-y-2">
                  <Label htmlFor="preview-assessment">Assessment</Label>
                  <Select
                    value={selectedAssessmentId}
                    onValueChange={handlePreviewAssessmentChange}
                  >
                    <SelectTrigger id="preview-assessment">
                      <SelectValue placeholder="Select assessment for preview" />
                    </SelectTrigger>
                    <SelectContent>
                      {groupedHybridAssessments.length === 0 ? (
                        <SelectItem value="no-hybrid-assessments" disabled>
                          No hybrid assessments
                        </SelectItem>
                      ) : (
                        groupedHybridAssessments.map((group, groupIndex) => (
                          <SelectGroup key={group.courseId}>
                            {groupIndex > 0 && <SelectSeparator />}
                            <SelectLabel>{group.courseTitle}</SelectLabel>
                            {group.assessments.map((assessment) => (
                              <SelectItem
                                key={assessment.id}
                                value={assessment.id}
                                disabled={!assessment.hasSampleSubmission}
                              >
                                <span className="flex w-full items-center justify-between gap-3">
                                  <span className="truncate">
                                    {assessment.title}
                                  </span>
                                  {!assessment.hasSampleSubmission && (
                                    <span className="shrink-0 text-xs text-muted-foreground">
                                      Preview not available
                                    </span>
                                  )}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex-1 flex flex-col min-h-0">
                <Label htmlFor="instructions">Instructions</Label>
                <InstructionsEditor
                  value={instructions}
                  onChange={setInstructions}
                  mediaFiles={mediaFiles}
                  boards={boards}
                  onUploadMedia={() => mediaUploadInputRef.current?.click()}
                  placeholder="Enter detailed instructions for the agent"
                />
              </div>

              <AgentFilesSection
                contextFiles={contextFiles}
                mediaFiles={mediaFiles}
                boards={boards}
                isUploadingFiles={isUploadingFiles}
                onAddContext={() => setIsAddContextDialogOpen(true)}
                onAddBoard={() => setIsAddBoardDialogOpen(true)}
                onEditBoard={(board) => setEditingBoardId(board.id)}
                onRemoveBoard={handleRemoveBoard}
                onRemoveFile={handleRemoveFile}
              />
            </div>
          </div>

          <AgentPlaybookPreview
            assessmentType={assessmentType}
            isTesting={isTesting}
            playbook={playbook}
            onUpdatePlaybook={handleTestAgent}
            canUpdatePlaybook={canPreviewPlaybook}
            emptyMessage={previewEmptyMessage}
            sampleSubmission={sampleSubmission}
            isLoadingSampleSubmission={isLoadingSampleSubmission}
            sampleSubmissionMessage={sampleSubmissionMessage}
          />
        </div>
      </div>
    </div>
  );
};
